import { useEffect, useState } from 'react';
import {
  SelectPeopleModal,
  type SelectPeopleSection,
  type SelectPersonRow,
} from 'src/design-system/SelectPeopleModal';
import { FinancialCategory } from 'src/financial/financial-data/hooks/useFinancialItems';
import { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';

export type OwnerSelection = { client_id: string } | { external_name: string };

const categoryToItemNoun: Record<FinancialCategory, string> = {
  SPENDING_ACCOUNT: 'spending account',
  ASSET: 'asset',
  LIABILITY: 'liability',
  PROTECTION: 'protection policy',
};

type FamilyOwnerModalProps = {
  opened: boolean;
  onClose: () => void;
  members: FamilyMember[];
  currentClientId: string;
  /** Names the thing being added in the title. Absent only while the picker is closed. */
  category?: FinancialCategory;
  /** Members ticked when the picker opens. Defaults to the client the flow is anchored on. */
  initialClientIds?: string[];
  onContinue: (owners: OwnerSelection[]) => void;
};

type ExternalOwner = { id: string; name: string };

export const FamilyOwnerModal = ({
  opened,
  onClose,
  members,
  currentClientId,
  category,
  initialClientIds,
  onContinue,
}: FamilyOwnerModalProps) => {
  // Opening resets the picker to its preselection. Callers rebuild that array on every render,
  // so the reset keys off its contents rather than its identity.
  const preselectedKey = (initialClientIds ?? [currentClientId]).join(',');
  const preselectedIds = () => (preselectedKey === '' ? [] : preselectedKey.split(','));
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>(preselectedIds);
  const [externals, setExternals] = useState<ExternalOwner[]>([]);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    if (!opened) return;
    setSelectedClientIds(preselectedKey === '' ? [] : preselectedKey.split(','));
    setExternals([]);
    setSeq(0);
  }, [opened, preselectedKey]);

  const toggleClient = (clientId: string) =>
    setSelectedClientIds(previous =>
      previous.includes(clientId)
        ? previous.filter(id => id !== clientId)
        : [...previous, clientId],
    );

  const memberRows: SelectPersonRow[] = members.map(member => ({
    id: member.clientId,
    name: `${member.client.firstName} ${member.client.lastName}`,
    role: member.roleLabel ?? (member.isMain ? 'Main' : ''),
    checked: selectedClientIds.includes(member.clientId),
    onToggle: () => toggleClient(member.clientId),
  }));

  const externalRows: SelectPersonRow[] = externals.map(external => ({
    id: external.id,
    name: external.name,
    role: 'External',
    checked: true,
    onToggle: () => setExternals(previous => previous.filter(item => item.id !== external.id)),
  }));

  const itemNoun = category ? categoryToItemNoun[category] : 'item';
  const hasFamilyOwner = selectedClientIds.length > 0;

  const section: SelectPeopleSection = {
    key: 'owners',
    title: 'Owners',
    requiredMessage:
      externals.length > 0
        ? `Choose at least one family member. External owners cannot own this ${itemNoun} on their own.`
        : 'Choose at least one family member.',
    addLabel: 'Add external owner',
    searchPlaceholder: 'External owner name',
    noneLabel: 'Add an external owner',
    allowFreeText: true,
    rows: [...memberRows, ...externalRows],
    onAddFreeText: typed => {
      setExternals(previous => [...previous, { id: `external:${seq}`, name: typed }]);
      setSeq(previous => previous + 1);
    },
  };

  const handleConfirm = () => {
    const owners: OwnerSelection[] = [
      ...selectedClientIds.map(id => ({ client_id: id })),
      ...externals.map(external => ({ external_name: external.name })),
    ];
    onContinue(owners);
  };

  return (
    <SelectPeopleModal
      opened={opened}
      onClose={onClose}
      title={`Select family owners of this ${itemNoun}`}
      subtitle="You can also add external owners here"
      confirmLabel="Continue"
      confirmDisabled={!hasFamilyOwner}
      sections={[section]}
      validationTriggered={!hasFamilyOwner}
      onConfirm={handleConfirm}
    />
  );
};
